import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../styles/Faq.css";

type FaqCategory =
  | "Getting started"
  | "Features & views"
  | "Customization"
  | "AI & automation"
  | "Customer portal"
  | "Teams & permissions"
  | "Integrations"
  | "Plans & billing"
  | "Security & privacy"
  | "Support";

// Explicit sidebar / section ordering, independent of where items are
// declared in the list below.
const CATEGORY_ORDER: FaqCategory[] = [
  "Getting started",
  "Features & views",
  "Customization",
  "AI & automation",
  "Customer portal",
  "Teams & permissions",
  "Integrations",
  "Plans & billing",
  "Security & privacy",
  "Support",
];

type FaqItem = {
  id: string;
  category: FaqCategory;
  question: string;
  answerText: string;
  answer: React.ReactNode;
  keywords?: string[];
};

const normalize = (s: string) => s.toLowerCase().trim();

const FaqPage: React.FC = () => {
  const webappUrl =
    process.env.REACT_APP_PABSYNC_WEBAPP_URL || "https://app.pabsync.com";

  const signupUrl = `${webappUrl}/register?plan=free&src=website`;

  const faqs: FaqItem[] = useMemo(
    () => [
      {
        id: "what-is-pabsync",
        category: "Getting started",
        question: "What is PabSync?",
        answerText:
          "PabSync is a work management platform that helps you capture projects, organize tasks, and keep teams aligned. It’s designed to keep workflows simple and structured so people and AI helpers can collaborate with clear context.",
        answer: (
          <>
            <p>
              PabSync is a work management platform that helps you capture projects,
              organize tasks, and keep teams aligned.
            </p>
            <p>
              It’s designed to keep workflows simple and structured so people and AI
              helpers can collaborate with clear context.
            </p>
          </>
        ),
        keywords: ["projects", "tasks", "work management", "ai"],
      },
      {
        id: "who-is-it-for",
        category: "Getting started",
        question: "Who is PabSync for?",
        answerText:
          "PabSync is for individuals who want a focused task system, and for teams that need clear ownership, lightweight coordination, and an upgrade path as they grow.",
        answer: (
          <>
            <p>
              PabSync is built for individuals who want a focused task system and for
              teams that need clear ownership and lightweight coordination.
            </p>
            <p>
              Start small, then upgrade when you need team features like user
              management and org-level controls.
            </p>
          </>
        ),
        keywords: ["individual", "team", "organization"],
      },
      {
        id: "how-do-i-get-started",
        category: "Getting started",
        question: "How do I get started?",
        answerText:
          "Create a free account, then create a project and start capturing tasks. If you’re evaluating for a team, start with Free or Starter and upgrade when ready.",
        answer: (
          <>
            <p>
              Create a free account, then create a project and start capturing
              tasks.
            </p>
            <p>
              If you’re evaluating for a team, start with Free or Starter and
              upgrade when you’re ready.
            </p>
            <p>
              <a className="faq-link" href={signupUrl}>
                Create a free account
              </a>
              <span className="faq-link-sep">•</span>
              <a className="faq-link" href="/pricing">
                View pricing
              </a>
            </p>
          </>
        ),
        keywords: ["sign up", "register", "free"],
      },
      {
        id: "where-do-i-log-in",
        category: "Getting started",
        question: "Where do I log in?",
        answerText:
          "PabSync is web-based. Use the web app to log in and manage your account.",
        answer: (
          <>
            <p>PabSync is web-based. Use the web app to log in and manage your account.</p>
            <p>
              <a className="faq-link" href={webappUrl}>
                Open the web app
              </a>
            </p>
          </>
        ),
        keywords: ["login", "web app", "app"],
      },
      {
        id: "is-there-a-free-plan",
        category: "Plans & billing",
        question: "Is there a Free plan? Do I need a credit card?",
        answerText:
          "Yes. You can start on the Free plan, and no credit card is required to create a Free account.",
        answer: (
          <>
            <p>
              Yes — you can start on the Free plan.
              <br />
              No credit card is required to create a Free account.
            </p>
          </>
        ),
        keywords: ["free", "credit card"],
      },
      {
        id: "plans-differences",
        category: "Plans & billing",
        question: "What’s the difference between Free, Starter, and Team?",
        answerText:
          "Free is for trying PabSync with a small setup. Starter is for solo power users who want unlimited projects. Team adds collaboration features like user management and per-user billing.",
        answer: (
          <>
            <p>
              <strong>Free</strong> is for trying PabSync with a small setup.
            </p>
            <p>
              <strong>Starter</strong> is for solo power users who want unlimited
              projects.
            </p>
            <p>
              <strong>Team</strong> adds collaboration features (like user
              management) and per-user billing.
            </p>
            <p>
              For the most up-to-date plan details, see <a href="/pricing">Pricing</a>.
            </p>
          </>
        ),
        keywords: ["pricing", "starter", "team"],
      },
      {
        id: "who-can-manage-billing",
        category: "Plans & billing",
        question: "Who can manage billing and subscription changes?",
        answerText:
          "To protect organizations, subscription and billing changes are limited to the account owner and/or organization administrators.",
        answer: (
          <>
            <p>
              To protect organizations, subscription and billing changes are limited
              to the account owner and/or organization administrators.
            </p>
            <p>
              If you’re not an owner/admin, you can still use the product — but you
              won’t see controls to change billing.
            </p>
          </>
        ),
        keywords: ["billing", "owner", "admin"],
      },
      {
        id: "payments-provider",
        category: "Plans & billing",
        question: "How do payments work?",
        answerText:
          "Paid plans are billed through PayPal. You’ll complete checkout in the web app.",
        answer: (
          <>
            <p>
              Paid plans are billed through PayPal, and you’ll complete checkout in
              the web app.
            </p>
            <p>
              If you have questions about billing, the fastest path is usually your
              account’s billing page in the app, or <a href="/contact">contact us</a>.
            </p>
          </>
        ),
        keywords: ["paypal", "checkout", "billing"],
      },
      {
        id: "team-seats",
        category: "Teams & permissions",
        question: "How do seats work on the Team plan?",
        answerText:
          "Team uses per-user seats for active members. Seat billing adjusts at renewal and can be calculated from the peak active member count during the billing period.",
        answer: (
          <>
            <p>
              Team uses per-user seats for active members.
            </p>
            <p>
              Seat billing adjusts at renewal and can be calculated from the peak
              active member count during the billing period.
            </p>
            <p>
              In the app, seat details are shown to account owners/admins.
            </p>
          </>
        ),
        keywords: ["seats", "team", "members", "billing"],
      },
      {
        id: "org-and-personal",
        category: "Teams & permissions",
        question: "Do you support personal and organization accounts?",
        answerText:
          "Yes. PabSync supports both personal accounts and organization-style accounts, with roles and permissions for team management.",
        answer: (
          <>
            <p>
              Yes — PabSync supports both personal accounts and
              organization-style accounts.
            </p>
            <p>
              Team-style accounts include roles and permissions so you can control
              who can manage members and billing.
            </p>
          </>
        ),
        keywords: ["org", "personal", "roles"],
      },
      {
        id: "slack-integration",
        category: "Integrations",
        question: "Do you support Slack integration?",
        answerText:
          "Yes. PabSync supports Slack integration so teams can connect work to the channels where they communicate, connect a workspace, and configure channels for updates.",
        answer: (
          <>
            <p>
              Yes — PabSync supports Slack integration so teams can connect work to
              the channels where they communicate.
            </p>
            <p>
              You can connect a workspace and configure channels for updates.
              Workspace availability depends on your plan. Free is typically limited
              to a single workspace; paid plans unlock more flexibility.
            </p>
          </>
        ),
        keywords: ["slack", "workspace", "channels", "oauth"],
      },
      {
        id: "notifications",
        category: "Integrations",
        question: "What notifications can I receive?",
        answerText:
          "You can configure notification preferences in the app, including Slack notifications and digest-style updates.",
        answer: (
          <>
            <p>
              You can configure notification preferences in the app, including:
            </p>
            <ul>
              <li>Slack notifications (e.g., project updates and task activity)</li>
              <li>Digest-style updates (daily and/or weekly)</li>
            </ul>
            <p>
              Notification controls are available in your account settings.
            </p>
          </>
        ),
        keywords: ["notifications", "digest", "slack"],
      },
      {
        id: "custom-fields-objects",
        category: "Customization",
        question: "Can I use custom fields or custom objects?",
        answerText:
          "Yes. Paid tiers include custom fields and custom objects so you can adapt PabSync to your workflow without forcing everything into a single rigid template.",
        answer: (
          <>
            <p>
              Yes — paid tiers include custom fields and custom objects so you can
              adapt PabSync to your workflow.
            </p>
            <p>
              This helps teams track the details they care about without forcing
              everything into a single rigid template.
            </p>
          </>
        ),
        keywords: ["custom fields", "custom objects"],
      },
      {
        id: "is-my-data-secure",
        category: "Security & privacy",
        question: "Is my data secure?",
        answerText:
          "PabSync is built with security in mind: account-scoped multi-tenant isolation, granular role-based access control, session tokens kept in httpOnly cookies (never exposed to the browser), HTTPS in transit, and a full activity audit trail. AI runs on self-hosted models, so your content isn't sent to third-party AI providers.",
        answer: (
          <>
            <p>Security is built into how PabSync works:</p>
            <ul>
              <li>
                <strong>Account-scoped isolation</strong> — every request is
                scoped to your account in our multi-tenant model.
              </li>
              <li>
                <strong>Role-based access control</strong> — granular roles and
                permissions decide who can see and change what.
              </li>
              <li>
                <strong>Secure sessions</strong> — auth tokens live in httpOnly
                cookies and are never exposed to JavaScript in the browser.
              </li>
              <li>
                <strong>Full audit trail</strong> — every change to a task or
                record is logged.
              </li>
              <li>
                <strong>Private AI</strong> — AI runs on self-hosted models, so
                your content isn’t sent to third-party AI providers.
              </li>
            </ul>
            <p>
              We also recommend a strong, unique password. Learn more on our{" "}
              <a href="/security">Security</a> page.
            </p>
          </>
        ),
        keywords: [
          "security",
          "https",
          "encryption",
          "roles",
          "permissions",
          "audit",
          "isolation",
          "ai privacy",
        ],
      },
      {
        id: "privacy-and-data",
        category: "Security & privacy",
        question: "Where can I read about privacy, cookies, and data handling?",
        answerText:
          "See our Privacy Statement and Cookies Notice for details about what we collect and how we use it. For vendor information, see Subprocessors.",
        answer: (
          <>
            <p>
              For details about what we collect and how we use it, see our&nbsp;
              <a className="faq-link" href="/privacy">
                Privacy Statement
              </a>
              <span className="faq-link-sep">•</span>
              <a className="faq-link" href="/cookies">
                Cookies Notice
              </a>
              <span className="faq-link-sep">•</span>
              <a className="faq-link" href="/subprocessors">
                Subprocessors
              </a>
            </p>
          </>
        ),
        keywords: ["privacy", "cookies", "subprocessors"],
      },
      {
        id: "delete-account",
        category: "Security & privacy",
        question: "How do I delete my account?",
        answerText:
          "You can request account deletion from inside the app. Deletion deactivates access immediately. Account data may be retained for 15 days before permanent deletion.",
        answer: (
          <>
            <p>You can request account deletion from inside the app.</p>
            <p>
              Account deletion deactivates access immediately. Account data may be
              retained for 15 days before permanent deletion.
            </p>
            <p>
              For general privacy and retention details, see our&nbsp;
              <a className="faq-link" href="/privacy">
                Privacy Statement
              </a>
              .
            </p>
            <p>
              If you need help, <a href="/contact">contact us</a>.
            </p>
          </>
        ),
        keywords: ["delete", "retention", "15 days"],
      },
      {
        id: "privacy-requests",
        category: "Security & privacy",
        question: "How do I submit a privacy request (access, deletion, export)?",
        answerText:
          "Depending on where you live, you may have rights to access, delete, or export your information. Use the Privacy Requests page for details.",
        answer: (
          <>
            <p>
              Depending on where you live, you may have rights to access, delete, or
              export your information.
            </p>
            <p>
              See <a href="/privacy-requests">Privacy Requests</a> for details.
            </p>
          </>
        ),
        keywords: ["gdpr", "ccpa", "export"],
      },
      {
        id: "support",
        category: "Support",
        question: "How do I contact support?",
        answerText:
          "Use the Contact page to send a message. Typical response time is 1–2 business days. For billing changes, use the self-serve billing pages in the app.",
        answer: (
          <>
            <p>
              Use <a href="/contact">Contact</a> to send a message.
            </p>
            <p>
              Typical response time is 1–2 business days. For billing changes, use
              the self-serve billing pages in the app.
            </p>
          </>
        ),
        keywords: ["contact", "help"],
      },

      // ---- Features & views ----
      {
        id: "views-overview",
        category: "Features & views",
        question: "What views does PabSync give me for managing work?",
        answerText:
          "Every project offers a Kanban board, a task list, a backlog, and a calendar, plus a project summary. You can switch between them to work the way you prefer.",
        answer: (
          <>
            <p>Every project gives you several ways to work:</p>
            <ul>
              <li>
                <strong>Kanban board</strong> — drag-and-drop with swim-lanes and
                WIP limits.
              </li>
              <li>
                <strong>List</strong> — a sortable, filterable table with saved
                filters.
              </li>
              <li>
                <strong>Backlog</strong> — groom and prioritize upcoming work.
              </li>
              <li>
                <strong>Calendar</strong> — see tasks by due date.
              </li>
              <li>
                <strong>Summary</strong> — an overview of the project at a glance.
              </li>
            </ul>
          </>
        ),
        keywords: ["views", "board", "list", "backlog", "calendar", "kanban"],
      },
      {
        id: "kanban",
        category: "Features & views",
        question: "How does the Kanban board work?",
        answerText:
          "Drag-and-drop tasks across statuses, group them with swim-lanes, cap work in progress with WIP limits, and switch on bulk-edit mode to update many tasks at once. Changes appear in real time for the whole team.",
        answer: (
          <>
            <p>
              The board supports drag-and-drop across statuses, swim-lanes to
              group work, WIP limits to keep columns from overflowing, and a
              bulk-edit mode for updating many tasks at once.
            </p>
            <p>Updates sync in real time, so teammates see changes live.</p>
          </>
        ),
        keywords: ["kanban", "board", "drag", "wip", "swim-lane", "bulk edit"],
      },
      {
        id: "task-anatomy",
        category: "Features & views",
        question: "What can a task hold?",
        answerText:
          "A task can have a status, assignee, priority, due dates, a rich description, subtasks, linked/related tasks, comments with @mentions, file attachments, time tracking, labels, custom fields, and a complete activity log.",
        answer: (
          <>
            <p>Tasks are rich by default. Each one can include:</p>
            <ul>
              <li>Status, assignee, priority, and due dates</li>
              <li>Subtasks and linked / related tasks</li>
              <li>Comments with @mentions and file attachments</li>
              <li>Time tracking, labels, and custom fields</li>
              <li>A full activity log of every change</li>
            </ul>
          </>
        ),
        keywords: [
          "task",
          "subtask",
          "attachment",
          "comment",
          "time tracking",
          "labels",
          "activity",
        ],
      },
      {
        id: "search-filters",
        category: "Features & views",
        question: "Can I search and filter across my work?",
        answerText:
          "Yes. Global search covers your tasks and records with both full-text and semantic (meaning-based) search. Within a project you can filter tasks and save filters to reuse.",
        answer: (
          <>
            <p>
              Global search spans your tasks and records, with both full-text and
              semantic (meaning-based) matching.
            </p>
            <p>
              Within a project you can filter the task list and save filters for
              quick reuse.
            </p>
          </>
        ),
        keywords: ["search", "filter", "saved filters", "semantic", "full-text"],
      },
      {
        id: "notifications-realtime",
        category: "Features & views",
        question: "How do notifications and real-time updates work?",
        answerText:
          "You get an in-app notification feed with configurable preferences — in-app, Slack, and daily or weekly digests. Boards and tasks update live, so teammates see changes as they happen.",
        answer: (
          <>
            <p>
              A built-in notification feed keeps you current, with preferences for
              in-app alerts, Slack, and daily or weekly digests.
            </p>
            <p>Boards and tasks also update in real time across the team.</p>
          </>
        ),
        keywords: ["notifications", "real-time", "live", "digest", "slack"],
      },
      {
        id: "dashboards-reports",
        category: "Features & views",
        question: "Can I build dashboards and reports?",
        answerText:
          "Yes. Account dashboards let you add configurable widgets — bar, line, pie, and table charts — to report on your data.",
        answer: (
          <p>
            Account dashboards let you assemble configurable widgets — bar, line,
            pie, and table charts — to report on the data that matters to you.
          </p>
        ),
        keywords: ["dashboard", "report", "chart", "widget", "analytics"],
      },
      {
        id: "mobile-dark",
        category: "Features & views",
        question: "Does PabSync work on mobile and support dark mode?",
        answerText:
          "Yes. The app is responsive across desktop, tablet, and mobile, and supports light, dark, and system themes.",
        answer: (
          <p>
            PabSync is responsive across desktop, tablet, and mobile, and supports
            light, dark, and system color themes.
          </p>
        ),
        keywords: ["mobile", "responsive", "dark mode", "theme"],
      },

      // ---- Customization ----
      {
        id: "custom-objects-crm",
        category: "Customization",
        question: "What are custom objects and the CRM starter pack?",
        answerText:
          "Beyond tasks, you can define your own object types with their own records — like a lightweight CRM. PabSync ships a CRM starter pack (Contacts, Companies, Deals) you can install in one click.",
        answer: (
          <>
            <p>
              Custom objects let you model data beyond tasks — define your own
              object types and store records against them, like a lightweight CRM.
            </p>
            <p>
              A CRM starter pack (Contacts, Companies, and Deals) can be installed
              in one click to get going quickly.
            </p>
          </>
        ),
        keywords: [
          "custom objects",
          "crm",
          "records",
          "contacts",
          "companies",
          "deals",
        ],
      },
      {
        id: "templates-layout",
        category: "Customization",
        question: "Can I reuse setups and customize how tasks look?",
        answerText:
          "Yes. Task templates let you spin up recurring work quickly, and the task layout builder lets admins choose which fields appear and where.",
        answer: (
          <p>
            Task templates make recurring work quick to create, and the task
            layout builder lets admins arrange which fields appear on a task and
            where.
          </p>
        ),
        keywords: ["templates", "task layout", "fields", "reuse"],
      },

      // ---- AI & automation ----
      {
        id: "automations",
        category: "AI & automation",
        question: "What can automations do?",
        answerText:
          "Automations are event-driven rules: a trigger (like a task being created or changed) plus optional conditions runs actions — set status, assign a user, update a field, create a record, send a Slack message or email, call AI, or hit a webhook. Ready-made presets help you start fast.",
        answer: (
          <>
            <p>
              Automations run event-driven rules: a trigger plus optional
              conditions fires one or more actions.
            </p>
            <p>Actions include:</p>
            <ul>
              <li>Set status, assign a user, or update a field</li>
              <li>Create a record</li>
              <li>Send a Slack message or an email</li>
              <li>Call AI or trigger a webhook</li>
            </ul>
            <p>Ready-made presets get you started without building from scratch.</p>
          </>
        ),
        keywords: [
          "automation",
          "workflow",
          "trigger",
          "action",
          "webhook",
          "rules",
        ],
      },
      {
        id: "ai-features",
        category: "AI & automation",
        question: "What AI features are included?",
        answerText:
          "PabSync includes AI assist on tasks (summarize, draft, and enrich), auto-computed AI attributes, and “Ask PabSync” — an assistant that answers questions about your workspace and cites its sources.",
        answer: (
          <>
            <p>PabSync has AI built in across the product:</p>
            <ul>
              <li>
                <strong>AI assist on tasks</strong> — summarize, draft, and enrich
                content.
              </li>
              <li>
                <strong>AI attributes</strong> — fields that compute themselves.
              </li>
              <li>
                <strong>Ask PabSync</strong> — an assistant that answers questions
                about your workspace and cites its sources.
              </li>
            </ul>
          </>
        ),
        keywords: ["ai", "assistant", "ask pabsync", "summarize", "enrich"],
      },
      {
        id: "ai-cost-privacy",
        category: "AI & automation",
        question: "Does AI cost extra, and is my data private?",
        answerText:
          "AI runs on self-hosted models, so there’s no per-seat AI fee and your content isn’t sent to third-party AI providers.",
        answer: (
          <p>
            AI runs on self-hosted models — so there’s no per-seat AI fee, and your
            content isn’t sent to third-party AI providers.
          </p>
        ),
        keywords: ["ai cost", "ai privacy", "self-hosted", "no per-seat"],
      },

      // ---- Customer portal ----
      {
        id: "customer-portal",
        category: "Customer portal",
        question: "What is the customer portal?",
        answerText:
          "The customer portal gives your clients a branded, scoped view of their project. They can follow progress and submit requests without access to your full workspace.",
        answer: (
          <p>
            The customer portal gives clients a branded, scoped view of their
            project — they can follow progress and submit requests without seeing
            your full workspace.
          </p>
        ),
        keywords: ["customer portal", "client", "external", "requests"],
      },

      // ---- Teams & permissions ----
      {
        id: "roles-permissions",
        category: "Teams & permissions",
        question: "How do roles and permissions work?",
        answerText:
          "You manage members at the account level (invite people, assign roles) and set per-project roles such as administrator and member. Access to features is controlled by role and by your plan.",
        answer: (
          <p>
            Manage members at the account level — invite people and assign
            roles — and set per-project roles such as administrator and member.
            Feature access is governed by both role and plan.
          </p>
        ),
        keywords: ["roles", "permissions", "admin", "members", "access"],
      },

      // ---- Integrations ----
      {
        id: "integrations-overview",
        category: "Integrations",
        question: "Which integrations does PabSync support?",
        answerText:
          "Live today: Slack and Google Workspace (Drive, Calendar, and Gmail). More are on the way in the Apps Center, including Microsoft 365, Microsoft Teams, Zoom, Dropbox, OneDrive, Trello, and GitHub.",
        answer: (
          <>
            <p>
              <strong>Live today:</strong> Slack, and Google Workspace (Drive,
              Calendar, and Gmail).
            </p>
            <p>
              <strong>Coming soon</strong> in the Apps Center: Microsoft 365,
              Microsoft Teams, Zoom, Dropbox, OneDrive, Trello, and GitHub.
            </p>
          </>
        ),
        keywords: [
          "integrations",
          "apps",
          "slack",
          "google",
          "microsoft",
          "github",
          "trello",
          "zoom",
        ],
      },
      {
        id: "google-workspace",
        category: "Integrations",
        question: "What does the Google Workspace integration do?",
        answerText:
          "Attach files directly from Google Drive, view and create Google Calendar events, and send email through Gmail — without leaving PabSync.",
        answer: (
          <p>
            With Google Workspace connected you can attach files from Google Drive,
            view and create Google Calendar events, and send email through Gmail —
            all without leaving PabSync.
          </p>
        ),
        keywords: ["google", "drive", "calendar", "gmail", "workspace"],
      },
    ],
    [signupUrl, webappUrl]
  );

  const categories: FaqCategory[] = useMemo(() => {
    const present = new Set(faqs.map((f) => f.category));
    return CATEGORY_ORDER.filter((c) => present.has(c));
  }, [faqs]);

  const [query, setQuery] = useState<string>("");
  const [activeCategory, setActiveCategory] = useState<FaqCategory | "All">("All");

  // When searching, we auto-expand matching results for convenience, but still
  // allow the user to toggle items open/closed.
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  const filteredFaqs = useMemo(() => {
    const q = normalize(query);

    return faqs.filter((f) => {
      if (activeCategory !== "All" && f.category !== activeCategory) return false;
      if (!q) return true;

      const hay = [
        f.question,
        f.answerText,
        f.category,
        ...(f.keywords || []),
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [faqs, query, activeCategory]);

  useEffect(() => {
    const q = normalize(query);
    if (!q) {
      setOpenIds({});
      return;
    }

    const next: Record<string, boolean> = {};
    for (const f of filteredFaqs) next[f.id] = true;
    setOpenIds(next);
  }, [query, filteredFaqs]);

  // Deep-linking: landing on /faq#some-id (or clicking a "Link to this
  // question" permalink) should reveal that question — clear any active
  // filter/search so it isn't hidden, open it, and scroll it into view.
  const [pendingHash, setPendingHash] = useState<string | null>(null);

  const handleHash = useCallback(() => {
    const id = decodeURIComponent((window.location.hash || "").replace(/^#/, ""));
    if (id && faqs.some((f) => f.id === id)) {
      setActiveCategory("All");
      setQuery("");
      setPendingHash(id);
    }
  }, [faqs]);

  useEffect(() => {
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, [handleHash]);

  // Once the target is rendered (after the filter/search reset above), open
  // and scroll to it. Runs after the state reset has committed the item.
  useEffect(() => {
    if (!pendingHash) return;
    const el = document.getElementById(pendingHash) as HTMLDetailsElement | null;
    if (!el) return;
    el.open = true;
    setOpenIds((prev) => ({ ...prev, [pendingHash]: true }));
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    el.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    setPendingHash(null);
  }, [pendingHash, filteredFaqs]);

  // Page SEO helpers (title, meta, canonical + FAQ schema)
  // Note: This is a client-rendered SPA. We set page metadata for user agents and
  // keep it tidy by restoring previous values on unmount.
  useEffect(() => {
    const cleanup: Array<() => void> = [];

    const upsertMetaNameWithRestore = (name: string, content: string) => {
      const selector = `meta[name="${name}"]`;
      const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
      const prev = existing?.getAttribute("content");
      const created = !existing;

      const el = existing || document.createElement("meta");
      if (created) {
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }

      el.setAttribute("content", content);

      cleanup.push(() => {
        if (created) {
          el.remove();
        } else if (prev == null) {
          el.removeAttribute("content");
        } else {
          el.setAttribute("content", prev);
        }
      });
    };

    const upsertMetaPropertyWithRestore = (property: string, content: string) => {
      const selector = `meta[property="${property}"]`;
      const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
      const prev = existing?.getAttribute("content");
      const created = !existing;

      const el = existing || document.createElement("meta");
      if (created) {
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }

      el.setAttribute("content", content);

      cleanup.push(() => {
        if (created) {
          el.remove();
        } else if (prev == null) {
          el.removeAttribute("content");
        } else {
          el.setAttribute("content", prev);
        }
      });
    };

    const upsertCanonicalWithRestore = (href: string) => {
      const existing = document.head.querySelector(
        "link[rel=canonical]"
      ) as HTMLLinkElement | null;
      const prev = existing?.getAttribute("href");
      const created = !existing;

      const el = existing || document.createElement("link");
      if (created) {
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }

      el.setAttribute("href", href);

      cleanup.push(() => {
        if (created) {
          el.remove();
        } else if (prev == null) {
          el.removeAttribute("href");
        } else {
          el.setAttribute("href", prev);
        }
      });
    };

    const title = "FAQ | PabSync";
    const description =
      "Frequently asked questions about PabSync — plans, billing, Slack integration, security, privacy, and getting started.";

    const prevTitle = document.title;
    document.title = title;
    cleanup.push(() => {
      document.title = prevTitle;
    });

    const canonical = (() => {
      try {
        const isProd = window.location.hostname.endsWith("pabsync.com");
        const origin = isProd ? "https://pabsync.com" : window.location.origin;
        return new URL("/faq", origin).toString();
      } catch {
        return "https://pabsync.com/faq";
      }
    })();

    upsertMetaNameWithRestore("description", description);
    upsertCanonicalWithRestore(canonical);
    upsertMetaPropertyWithRestore("og:title", title);
    upsertMetaPropertyWithRestore("og:description", description);
    upsertMetaPropertyWithRestore("og:url", canonical);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.answerText,
        },
      })),
    };

    const id = "pabsync-faq-jsonld";
    const existing = document.getElementById(id) as HTMLScriptElement | null;
    const prevJson = existing?.textContent;
    const created = !existing;

    const script = existing || document.createElement("script");
    if (created) {
      script.id = id;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);

    cleanup.push(() => {
      if (created) {
        script.remove();
      } else {
        script.textContent = prevJson ?? null;
      }
    });

    return () => {
      // Restore in reverse order of application
      for (let i = cleanup.length - 1; i >= 0; i--) cleanup[i]();
    };
  }, [faqs]);

  const grouped = useMemo(() => {
    const map = new Map<FaqCategory, FaqItem[]>();
    for (const c of categories) map.set(c, []);
    for (const f of filteredFaqs) map.get(f.category)?.push(f);
    return map;
  }, [categories, filteredFaqs]);

  return (
    <div className="faq-page">
      <main className="faq-container">
        <section className="faq-hero">
          <div className="faq-hero-copy">
            <div className="faq-kicker">Help center</div>
            <h1>Frequently asked questions</h1>
            <p>
              Quick answers to common questions about getting started, plans,
              billing, Slack integration, and security.
            </p>

            <div className="faq-search" role="search">
              <label className="faq-search-label" htmlFor="faqSearch">
                Search FAQs
              </label>
              <div className="faq-search-inputWrap">
                <svg
                  className="faq-search-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
                <input
                  id="faqSearch"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search: billing, Slack, Free plan, security…"
                  autoComplete="off"
                  aria-describedby="faqSearchStatus"
                />
                {query ? (
                  <button
                    className="faq-search-clear"
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            <div className="faq-tags" role="group" aria-label="Filter by category">
              <button
                type="button"
                className={`faq-tag ${activeCategory === "All" ? "is-active" : ""}`}
                aria-pressed={activeCategory === "All"}
                onClick={() => setActiveCategory("All")}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`faq-tag ${activeCategory === c ? "is-active" : ""}`}
                  aria-pressed={activeCategory === c}
                  onClick={() => setActiveCategory(c)}
                >
                  {c}
                </button>
              ))}
            </div>

            <div
              id="faqSearchStatus"
              className="faq-search-label"
              role="status"
              aria-live="polite"
            >
              {query
                ? `${filteredFaqs.length} result${
                    filteredFaqs.length === 1 ? "" : "s"
                  } for “${query}”`
                : ""}
            </div>
          </div>

          <aside className="faq-hero-card" aria-label="Quick links">
            <h3>Quick links</h3>
            <ul>
              <li>
                <a href="/pricing">Pricing</a>
              </li>
              <li>
                <a href="/security">Security</a>
              </li>
              <li>
                <a href="/privacy">Privacy</a>
              </li>
              <li>
                <a href="/contact">Contact</a>
              </li>
            </ul>
          </aside>
        </section>

        <section className="faq-content">
          <div className="faq-layout">
            <aside className="faq-sidebar" aria-label="FAQ categories">
              <div className="faq-sidebar-card">
                <div className="faq-sidebar-title">Browse</div>
                <div
                  className="faq-sidebar-links"
                  role="group"
                  aria-label="Browse by category"
                >
                  <button
                    type="button"
                    className={`faq-sideLink ${
                      activeCategory === "All" ? "is-active" : ""
                    }`}
                    aria-pressed={activeCategory === "All"}
                    onClick={() => setActiveCategory("All")}
                  >
                    All questions
                  </button>
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`faq-sideLink ${
                        activeCategory === c ? "is-active" : ""
                      }`}
                      aria-pressed={activeCategory === c}
                      onClick={() => setActiveCategory(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="faq-sidebar-note">
                  Can’t find what you need? <a href="/contact">Contact us</a>.
                </div>
              </div>
            </aside>

            <div className="faq-list" aria-label="FAQ items">
              {filteredFaqs.length === 0 ? (
                <div className="faq-empty">
                  <div className="faq-empty-title">No results</div>
                  <p>
                    Try a different search (e.g., <em>billing</em>, <em>Slack</em>,
                    <em> Free</em>) or clear the filters.
                  </p>
                  <button
                    type="button"
                    className="faq-btn faq-btn--ghost"
                    onClick={() => {
                      setQuery("");
                      setActiveCategory("All");
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              ) : null}

              {Array.from(grouped.entries()).map(([category, items]) => {
                if (!items.length) return null;
                return (
                  <section key={category} className="faq-section">
                    <h2 className="faq-section-title">{category}</h2>
                    <div className="faq-section-items">
                      {items.map((f) => (
                        <details
                          key={f.id}
                          id={f.id}
                          className="faq-item"
                          open={normalize(query) ? Boolean(openIds[f.id]) : undefined}
                          onToggle={(e) => {
                            if (!normalize(query)) return;
                            const el = e.currentTarget as HTMLDetailsElement;
                            setOpenIds((prev) => ({ ...prev, [f.id]: el.open }));
                          }}
                        >
                          <summary className="faq-q">
                            <span>{f.question}</span>
                            <span className="faq-marker" aria-hidden="true" />
                          </summary>
                          <div className="faq-a">{f.answer}</div>
                          <div className="faq-item-footer">
                            <a
                              className="faq-permalink"
                              href={`#${f.id}`}
                              aria-label={`Link to this question: ${f.question}`}
                            >
                              Link to this question
                            </a>
                          </div>
                        </details>
                      ))}
                    </div>
                  </section>
                );
              })}

              <section className="faq-cta" aria-label="FAQ call to action">
                <div>
                  <h3>Still have questions?</h3>
                  <p>
                    If you’re evaluating PabSync for your team or you need help
                    getting started, send us a message.
                  </p>
                  <div className="faq-cta-actions">
                    <a className="faq-btn" href="/contact">
                      Contact us
                    </a>
                    <a className="faq-btn faq-btn--ghost" href={signupUrl}>
                      Create free account
                    </a>
                  </div>
                </div>
                <div className="faq-cta-meta">
                  <div className="faq-cta-pill">Typical response: 1–2 business days</div>
                  <div className="faq-cta-pill">No credit card required for Free</div>
                </div>
              </section>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default FaqPage;
